export interface WeeklyCycleProps {
  id: string;
  workspaceId: number;
  startDate: Date;
  endDate: Date;
  isActive: boolean;
  createdAt: Date;
}

export class WeeklyCycle {
  private readonly props: WeeklyCycleProps;

  constructor(props: WeeklyCycleProps) {
    this.props = props;
  }

  get id(): string {
    return this.props.id;
  }

  get workspaceId(): number {
    return this.props.workspaceId;
  }

  get startDate(): Date {
    return this.props.startDate;
  }

  get endDate(): Date {
    return this.props.endDate;
  }

  get isActive(): boolean {
    return this.props.isActive;
  }

  /**
   * Check if cycle has ended
   */
  hasEnded(): boolean {
    return new Date() > this.props.endDate;
  }

  /**
   * Check if cycle is currently running
   */
  isCurrentlyActive(): boolean {
    const now = new Date();
    return this.props.isActive && now >= this.props.startDate && now <= this.props.endDate;
  }

  /**
   * Get remaining time in milliseconds
   */
  getRemainingTime(): number {
    if (this.hasEnded()) return 0;
    return this.props.endDate.getTime() - new Date().getTime();
  }

  /**
   * Get remaining days
   */
  getRemainingDays(): number {
    return Math.ceil(this.getRemainingTime() / (1000 * 60 * 60 * 24));
  }

  /**
   * Get remaining hours
   */
  getRemainingHours(): number {
    return Math.ceil(this.getRemainingTime() / (1000 * 60 * 60));
  }

  /**
   * Get formatted remaining time (e.g., "2d 5h" or "5h 30m")
   */
  getFormattedRemainingTime(): string {
    const remaining = this.getRemainingTime();
    if (remaining <= 0) return 'Terminado';

    const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
    const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return `${days}d ${hours}h`;
    }
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }

  /**
   * Get progress percentage (0-100)
   */
  getProgressPercentage(): number {
    const total = this.props.endDate.getTime() - this.props.startDate.getTime();
    const elapsed = new Date().getTime() - this.props.startDate.getTime();
    return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
  }

  /**
   * Get urgency level based on remaining time
   * - 'normal': > 2 days
   * - 'warning': 1-2 days
   * - 'critical': < 1 day
   */
  getUrgencyLevel(): 'normal' | 'warning' | 'critical' {
    const remainingDays = this.getRemainingDays();
    if (remainingDays <= 1) return 'critical';
    if (remainingDays <= 2) return 'warning';
    return 'normal';
  }

  /**
   * Get week number in the year
   */
  getWeekNumber(): number {
    const start = new Date(this.props.startDate);
    const firstDayOfYear = new Date(start.getFullYear(), 0, 1);
    const days = Math.floor(
      (start.getTime() - firstDayOfYear.getTime()) / (1000 * 60 * 60 * 24)
    );
    return Math.ceil((days + firstDayOfYear.getDay() + 1) / 7);
  }

  /**
   * Create a new weekly cycle starting on the next Monday
   */
  static createNextCycle(workspaceId: number): Omit<WeeklyCycleProps, 'id' | 'createdAt'> {
    const now = new Date();
    // Find next Monday
    const daysUntilMonday = (8 - now.getDay()) % 7 || 7;
    const nextMonday = new Date(now);
    nextMonday.setDate(now.getDate() + daysUntilMonday);
    nextMonday.setHours(0, 0, 0, 0);

    // Sunday 23:59:59
    const nextSunday = new Date(nextMonday);
    nextSunday.setDate(nextMonday.getDate() + 6);
    nextSunday.setHours(23, 59, 59, 999);

    return {
      workspaceId,
      startDate: nextMonday,
      endDate: nextSunday,
      isActive: true,
    };
  }

  toPlainObject(): WeeklyCycleProps {
    return { ...this.props };
  }
}
